/* Shapes a mapping result (GET /api/documents/:id/mapped) for the Mapped tab.
 *
 * Input:  the raw result `{ values, issues }` and the PINT-OM standard field catalogue.
 * Output: header rows (one value each), line and VAT breakdown rows (one value per
 *         line or rate group), and the mapping issues, all in catalogue order.
 *
 * Example:
 *   const view = mappedView(mapped.data, fields.data ?? []);
 *   view.header[0] // { key: "InvoiceNumber", label: "Invoice number", ibt: "IBT-001", values: ["WR-1"] }
 */

import type { StandardFieldDto } from "../../hub/mapping/types";

export type MappedRow = { key: string; label: string; ibt: string | null; values: string[] };
export type MappedIssue = { field: string; reason: string };
export type MappedView = {
  header: MappedRow[];
  lines: MappedRow[];
  taxes: MappedRow[];
  issues: MappedIssue[];
  lineCount: number;
  taxCount: number;
};

/** 381 and 261 are described by the credit-note catalogue; 383 keeps the invoice root. */
export function catalogueFor(values: Record<string, unknown>): "invoice" | "creditnote" {
  return values.InvoiceTypeCode === "381" || values.InvoiceTypeCode === "261" ? "creditnote" : "invoice";
}

/** The result's `values`; an older result without the wrapper is all values. */
export function mappedValues(data: Record<string, unknown> | undefined): Record<string, unknown> {
  const values = data?.values;
  if (values && typeof values === "object" && !Array.isArray(values)) return values as Record<string, unknown>;
  if (!data || "values" in data || "issues" in data) return {};
  return data;
}

function text(value: unknown): string {
  if (value === null || value === undefined) return "";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function issuesOf(data: Record<string, unknown> | undefined): MappedIssue[] {
  const raw = Array.isArray(data?.issues) ? (data.issues as Array<Record<string, unknown>>) : [];
  return raw.map((issue) => ({ field: text(issue.field_id ?? issue.fieldId), reason: text(issue.reason) }));
}

export function mappedView(data: Record<string, unknown> | undefined, fields: StandardFieldDto[]): MappedView {
  const values = mappedValues(data);
  const order = new Map(fields.map((field, index) => [field.key, index]));
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const view: MappedView = { header: [], lines: [], taxes: [], issues: issuesOf(data), lineCount: 0, taxCount: 0 };

  const keys = Object.keys(values).sort(
    (a, b) => (order.get(a) ?? Infinity) - (order.get(b) ?? Infinity) || a.localeCompare(b),
  );
  for (const key of keys) {
    const raw = values[key];
    const field = byKey.get(key);
    const row: MappedRow = {
      key,
      label: field?.title || key,
      ibt: field?.ibt ?? null,
      values: Array.isArray(raw) ? raw.map(text) : [text(raw)],
    };
    // A repeated value belongs to a line or a VAT group; an unknown list is read as a line.
    if (!Array.isArray(raw)) view.header.push(row);
    else if (field?.level === "tax") view.taxes.push(row);
    else view.lines.push(row);
  }
  view.lineCount = Math.max(0, ...view.lines.map((row) => row.values.length));
  view.taxCount = Math.max(0, ...view.taxes.map((row) => row.values.length));
  return view;
}
